import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, mapTo } from 'rxjs/operators';
import { PagedResponse } from '../core';
import {
  BundleApiService,
  GetMultiBundleRequest,
  GetMultiBundleResponse,
  ListBundlesRequest,
  ListBundlesResponse,
  PagedRequestOptions,
  UpsertBundleRequest,
} from '@vendasta/billing';
import { Bundle, bundleFromApi, BundleItem, bundleItemToApi, isBundle } from './bundle';

export interface ListBundleFilter {
  merchantId?: string;
}

@Injectable({ providedIn: 'root' })
export class BundleService {
  constructor(private bundleApiService: BundleApiService) {}

  upsert(
    merchantId: string,
    bundleId: string,
    name: string,
    hideBundleDetails: boolean,
    items: BundleItem[],
  ): Observable<boolean> {
    const request = new UpsertBundleRequest({
      merchantId: merchantId,
      bundleId: bundleId,
      name: name,
      hideBundleDetails: hideBundleDetails,
      items: (items || []).map((item) => bundleItemToApi(item)),
    });
    return this.bundleApiService.upsert(request).pipe(mapTo(true));
  }

  getMulti(merchantId: string, bundleIds: string[]): Observable<Map<string, Bundle>> {
    const request = new GetMultiBundleRequest({
      merchantId: merchantId,
      bundleIds: bundleIds,
    });
    return this.bundleApiService.getMulti(request).pipe(
      map((resp: GetMultiBundleResponse) => {
        const bundlesMap = new Map<string, Bundle>();
        if (resp && resp.bundles) {
          bundleIds.forEach((bundleId) => {
            const bundles = bundleFromApi(resp.bundles[bundleId]);
            if (bundles) {
              bundlesMap.set(bundleId, bundles);
            }
          });
        }
        return bundlesMap;
      }),
    );
  }

  list(
    filters: ListBundleFilter,
    searchTerm: string,
    cursor: string,
    pageSize: number,
  ): Observable<PagedResponse<Bundle>> {
    const request = new ListBundlesRequest({
      filters: filters,
      searchTerm: searchTerm,
      pagingOptions: new PagedRequestOptions({ cursor, pageSize }),
    });
    return this.bundleApiService.list(request).pipe(
      map((resp: ListBundlesResponse) => {
        const bundles = (resp.bundles || []).map((bundle) => bundleFromApi(bundle)).filter(isBundle);
        return new PagedResponse<Bundle>(bundles, resp.pagingMetadata.nextCursor, resp.pagingMetadata.hasMore);
      }),
    );
  }
}
